
import { Link } from 'react-router';
import * as React from 'react';

import { FlagSyncLogo } from '@/components/svgs/logo';
import {
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenu as ShadNavigationMenu,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';

import { cn } from '@/lib/utils';

export function NavigationMenu() {
  const classes = cn(
    navigationMenuTriggerStyle(),
    'focus:bg-halfmast-800/50 data-active   :bg-halfmast-700/50 data-[state=open]:bg-halfmast-900/50 focus:text-foreground',
  );

  return (
    <ShadNavigationMenu>
      <NavigationMenuList>
        <NavigationMenuItem>
          <NavigationMenuTrigger triggerMode="click" className={classes}>
            Platform
          </NavigationMenuTrigger>
          <NavigationMenuContent className="z-[999999]">
            <ExpandableMenuContent />
          </NavigationMenuContent>
        </NavigationMenuItem>

        <NavigationMenuItem>
          <NavigationMenuLink asChild className={classes}>
            <Link to="/pricing">Pricing</Link>
          </NavigationMenuLink>
        </NavigationMenuItem>
        <NavigationMenuItem>
          <NavigationMenuLink asChild className={classes}>
            <Link to="/book-demo">Get a Demo</Link>
          </NavigationMenuLink>
        </NavigationMenuItem>
      </NavigationMenuList>
    </ShadNavigationMenu>
  );
}

function ExpandableMenuContent() {
  return (
    <ul className="grid gap-3 p-4 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
      <li className="row-span-3">
        <NavigationMenuLink asChild>
          <a
            target="_blank"
            className="flex h-full w-full select-none flex-col justify-end rounded-md bg-linear-to-b from-primary/20 to-background/20 p-6 shadow-xl shadow-background no-underline outline-hidden focus:shadow-md"
            href="https://docs.flagsync.com/"
          >
            <span>
              <FlagSyncLogo className="h-8" />
            </span>
            <div className="mb-2 mt-4 text-lg font-medium">Documentation</div>
            <p className="text-sm leading-tight">
              Get started by checking out the docs.
            </p>
          </a>
        </NavigationMenuLink>
      </li>
      <ListItem
        href="https://docs.flagsync.com/experiments/overview"
        target="_blank"
        title="Experiments"
      >
        Learn how to run A/B tests with FlagSync.
      </ListItem>
      <ListItem
        target="_blank"
        href="https://docs.flagsync.com/events/overview"
        title="Events"
      >
        Discover how events can help you track user behavior.
      </ListItem>
      <ListItem
        href="https://docs.flagsync.com/sdks/setup"
        target="_blank"
        title="SDKs"
      >
        Head over to Github to find the FlagSync SDKs.
      </ListItem>
    </ul>
  );
}

const ListItem = React.forwardRef<
  React.ElementRef<'a'>,
  React.ComponentPropsWithoutRef<'a'>
>(({ className, title, children, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <a
          ref={ref}
          className={cn(
            'block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-hidden transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
            className,
          )}
          {...props}
        >
          <div className="text-sm font-medium leading-none">{title}</div>
          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
            {children}
          </p>
        </a>
      </NavigationMenuLink>
    </li>
  );
});

ListItem.displayName = 'ListItem';
